import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { selectBestInterviewer } from "@/components/interviewer-picker";
import { orpc } from "@/lib/rpc";
import type { AssignmentOption, StageInterviewersData } from "@/types/move-application";

/**
 * Builds AssignmentOption[] from stage interviewer data.
 * Shared between useInterviewerAssignment and useMoveApplication.
 */
export function buildAssignmentOptions(
  stageInterviewers: StageInterviewersData | undefined,
  stageId: string | null,
  excludeInterviewerId?: string | null,
  excludeGroupId?: string | null,
): AssignmentOption[] {
  if (!stageId || !stageInterviewers) return [];
  const stageData = stageInterviewers.find((s) => s.stageId === stageId);
  const employees = stageData?.employees || [];
  const groups = stageData?.groups || [];
  return [
    ...employees
      .filter((e) => !(excludeInterviewerId && e.id === excludeInterviewerId && !excludeGroupId))
      .map((e) => ({
        type: "individual" as const,
        id: e.id,
        label: e.fullName,
        interviewCount: e.interviewCount,
        schedulingLinks: e.schedulingLinks,
        email: e.corporateEmail,
      })),
    ...groups
      .filter((g) => !(excludeGroupId && g.id === excludeGroupId))
      .map((g) => ({
        type: "group" as const,
        id: g.id,
        label: g.members.map((m) => m.fullName).join(" + "),
        interviewCount: Math.max(...g.members.map((m) => m.interviewCount), 0),
        schedulingLink: g.schedulingLink,
        email: g.members[0]?.corporateEmail,
      })),
  ];
}

interface UseInterviewerAssignmentOptions {
  jobOpeningId: string;
  stageId: string;
  enabled: boolean;
  /** Filter out the current interviewer (for change-interviewer flows) */
  excludeInterviewerId?: string | null;
  excludeGroupId?: string | null;
}

/**
 * Hook that encapsulates the full interviewer assignment flow:
 * - Loads stage interviewers + all people
 * - Builds assignment options
 * - Manages selection state (interviewerId, selectedGroupId, assignmentMode)
 * - Auto-selects the best interviewer on open
 */
export function useInterviewerAssignment({
  jobOpeningId,
  stageId,
  enabled,
  excludeInterviewerId,
  excludeGroupId,
}: UseInterviewerAssignmentOptions) {
  const [interviewerId, setInterviewerId] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [assignmentMode, setAssignmentMode] = useState<"automatic" | "manual">("automatic");

  // Load stage interviewers
  const { data: stageInterviewers, isLoading: isLoadingInterviewers } = useQuery({
    ...orpc.jobOpenings.getStageInterviewers.queryOptions({ input: { id: jobOpeningId } }),
    enabled: enabled && !!jobOpeningId,
  });

  // Load all people (fallback for manual selection)
  const { data: people } = useQuery({
    ...orpc.dashboard.people.queryOptions(),
    enabled,
  });

  const assignmentOptions = useMemo(
    () => buildAssignmentOptions(stageInterviewers, stageId, excludeInterviewerId, excludeGroupId),
    [stageInterviewers, stageId, excludeInterviewerId, excludeGroupId],
  );

  // Auto-select best interviewer when options change
  useEffect(() => {
    if (!enabled || assignmentOptions.length === 0) return;
    selectBestInterviewer(assignmentOptions, setInterviewerId, setSelectedGroupId);
    setAssignmentMode("automatic");
  }, [enabled, assignmentOptions]);

  const reset = useCallback(() => {
    setInterviewerId("");
    setSelectedGroupId(null);
    setAssignmentMode("automatic");
  }, []);

  return {
    interviewerId,
    setInterviewerId,
    selectedGroupId,
    setSelectedGroupId,
    assignmentMode,
    setAssignmentMode,
    assignmentOptions,
    people,
    isLoading: isLoadingInterviewers,
    hasSelection: !!interviewerId || !!selectedGroupId,
    reset,
  };
}
